import {
  candidatesOnOffer,
  type HandoverKind,
  isOpenQuestion,
  PARTY_ROLE_LABELS,
  type Party,
  type PartyCandidate,
  type PartyRole,
  TENANCY_LABELS,
} from '@acasa/shared'

/**
 * The people onOffice links to the estate, offered for the two sides of the
 * handover.
 *
 * onOffice keeps FORMER tenants linked to a flat, so "everybody linked" is not
 * "everybody who is here today". The prefill takes only who it can be sure of;
 * everyone else is listed here for the agent to take — the agent on site knows
 * who is standing in the hallway, the CRM does not.
 */

type CandidateRole = PartyCandidate['role']

/** Only ever asked about renters, so it only has to read right for them. */
function openQuestion(kind: HandoverKind): string {
  return kind === 'rental_moveout' ? 'Wer zieht aus?' : 'Wer zieht ein?'
}

/** "Mieter / Übergeber" → "Mieter": the part a person would actually say. */
export function shortRoleLabel(kind: HandoverKind, role: PartyRole): string {
  const label = PARTY_ROLE_LABELS[kind][role]
  return label.split(' / ')[0] ?? label
}

export function PartyCandidates({
  kind,
  role,
  candidates,
  parties,
  onTake,
}: {
  kind: HandoverKind
  role: CandidateRole
  /** Every candidate from the endpoint; filtered here. */
  candidates: PartyCandidate[]
  parties: Party[]
  onTake: (candidate: PartyCandidate) => void
}) {
  const offered = candidatesOnOffer(role, candidates, parties)
  if (offered.length === 0) return null

  const open = isOpenQuestion(role, candidates, parties)

  return (
    <section
      aria-label={open ? undefined : `${shortRoleLabel(kind, role)} aus onOffice`}
      className={
        open
          ? 'border-brand/30 bg-brand-soft/40 border p-3 sm:p-4'
          : 'border-line border p-3 sm:p-4'
      }
    >
      {open ? (
        <div className="mb-3">
          <h4 className="font-display text-ink text-base">{openQuestion(kind)}</h4>
          <p className="text-muted mt-1 text-xs leading-relaxed">
            In onOffice sind Mieter und Vormieter mit diesem Objekt verknüpft. Wer von beiden
            gemeint ist, lässt sich daraus nicht ablesen — bitte die richtige Person übernehmen.
          </p>
        </div>
      ) : (
        <h4 className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">
          Ebenfalls in onOffice verknüpft · {shortRoleLabel(kind, role)}
        </h4>
      )}

      <ul className="divide-line divide-y">
        {offered.map((candidate) => {
          const who = candidate.name.trim() || 'Person ohne Namen'
          const tenancy = candidate.tenancy ? TENANCY_LABELS[candidate.tenancy] : null

          return (
            <li
              key={candidate.addressId}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 first:pt-0 last:pb-0"
            >
              <div className="min-w-0 flex-1 basis-48">
                <p className="text-ink flex flex-wrap items-center gap-x-2 gap-y-1 text-sm break-words">
                  <span>{who}</span>
                  {tenancy && (
                    <span
                      // "Vormieter" is what the agent decides on, so it gets
                      // the warm tint; a current tenant stays neutral. Tinted,
                      // not filled — it has to be noticed, not shout.
                      className={`border px-1.5 py-0.5 text-[10px] tracking-wide uppercase ${
                        candidate.tenancy === 'former'
                          ? 'border-warning/40 bg-warning/10 text-ink font-medium'
                          : 'border-line text-muted'
                      }`}
                    >
                      {tenancy}
                    </span>
                  )}
                </p>
                {candidate.address && (
                  <p className="text-muted mt-0.5 text-xs break-words">{candidate.address}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => onTake(candidate)}
                aria-label={`${who} übernehmen`}
                className="border-line text-muted hover:border-brand hover:text-brand bg-surface min-h-11 shrink-0 border px-4 text-xs transition-colors"
              >
                Übernehmen
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
